import { html, css } from 'lit';
import { QghBase } from './base.js';
import { isLive, fmtTime, fmtDate, scoreOrTime, translateCountry } from './utils.js';

export class QghTeam extends QghBase {
  static properties = {
    ...QghBase.properties,
    team:  { type: String },
    theme: { type: String },
    group: { type: String },
  };

  static styles = [
    ...QghBase.styles,
    css`
      :host {
        display: block;
        font-family: 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: var(--qgh-bg);
        border-radius: var(--qgh-radius);
      }
      .widget { background: var(--qgh-bg); color: var(--qgh-color); border-radius: var(--qgh-radius); overflow: hidden; }

      /* Team header */
      .team-header {
        display: flex; align-items: center; gap: 14px;
        padding: 16px 14px 14px; border-bottom: 1px solid var(--qgh-border);
        background: rgba(255,255,255,.03);
      }
      .team-logo-lg { width: 48px; height: 48px; object-fit: contain; flex-shrink: 0; }
      .team-logo-fb {
        width: 48px; height: 48px; border-radius: 50%;
        background: var(--qgh-fb-bg); display: flex; align-items: center;
        justify-content: center; font-size: 20px; font-weight: 700; color: var(--qgh-color);
        flex-shrink: 0;
      }
      .team-meta { flex: 1; min-width: 0; }
      .team-name-lg { font-size: 18px; font-weight: 700; margin-bottom: 4px; }
      .team-badges { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
      .badge {
        font-size: 9px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
        padding: 3px 8px; border-radius: 12px; background: var(--qgh-nav-bg);
        white-space: nowrap;
      }
      .badge.confed { background: var(--qgh-label-bg); color: #fff; }

      /* Next match */
      .section-title {
        font-size: 9px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
        opacity: .45; padding: 10px 14px 6px;
      }
      .next-match {
        display: flex; align-items: center; gap: 10px;
        padding: 8px 14px 10px; border-bottom: 1px solid var(--qgh-border);
      }
      .opp-logo { width: 28px; height: 28px; object-fit: contain; flex-shrink: 0; }
      .opp-logo-fb {
        width: 28px; height: 28px; border-radius: 50%;
        background: var(--qgh-fb-bg); display: flex; align-items: center;
        justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0;
      }
      .opp-info { flex: 1; min-width: 0; }
      .opp-name { font-size: 14px; font-weight: 700; }
      .opp-detail { font-size: 10px; opacity: .45; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .next-when {
        font-size: 12px; font-weight: 700; color: var(--qgh-accent);
        text-align: right; flex-shrink: 0;
      }

      /* Match list */
      .match-row {
        display: flex; align-items: center; gap: 8px;
        padding: 8px 14px; border-bottom: 1px solid var(--qgh-border); font-size: 12px;
      }
      .match-row:last-child { border-bottom: none; }
      .match-round { font-size: 10px; opacity: .4; min-width: 52px; flex-shrink: 0; }
      .match-opp { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
      .match-opp-logo { width: 16px; height: 16px; object-fit: contain; flex-shrink: 0; }
      .match-opp-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .match-time { font-size: 12px; opacity: .5; flex-shrink: 0; font-variant-numeric: tabular-nums; }
      .match-score { font-size: 12px; font-weight: 700; flex-shrink: 0; min-width: 28px; text-align: right; font-variant-numeric: tabular-nums; }
      .match-score.live { color: var(--qgh-accent); }

      .no-next { padding: 10px 14px 12px; font-size: 12px; opacity: .4; border-bottom: 1px solid var(--qgh-border); }

      .state-box { padding: 32px 16px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 12px; }
      .state-logo { height: 22px; width: auto; filter: var(--qgh-logo-filter); opacity: .45; }
      .state-msg { font-size: 12px; opacity: .45; }
      .sk { border-radius: 4px; background: var(--qgh-border); animation: shimmer 1.4s ease-in-out infinite; }
      .sk-circle { border-radius: 50%; }
      @keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:.9} }
    `,
  ];

  _params() {
    const p = {};
    if (this.team)   p.team   = this.team;
    if (this.league) p.league = this.league;
    if (this.season) p.season = this.season;
    p.timezone = this.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return p;
  }

  async _load() {
    if (!this.team) { this._loading = false; return; }
    await super._load();
  }

  _teamInfo() {
    const id = Number(this.team);
    for (const f of this._fixtures) {
      if (f.teams.home.id === id) return f.teams.home;
      if (f.teams.away.id === id) return f.teams.away;
    }
    return null;
  }

  _nextMatch() {
    return this._fixtures
      .filter(f => f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD')
      .sort((a, b) => new Date(a.fixture.date) - new Date(b.fixture.date))[0] || null;
  }

  _opponent(f) {
    const id = Number(this.team);
    return f.teams.home.id === id ? f.teams.away : f.teams.home;
  }

  _whenLabel(f) {
    const d    = new Date(f.fixture.date);
    const now  = new Date();
    const diff = Math.round((d - now) / 86400000);
    if (diff === 0) return 'Hoy';
    if (diff === 1) return 'Mañana';
    return fmtDate(f.fixture.date);
  }

  _roundShort(round) {
    if (!round) return '';
    // Check for specific group (e.g. "Group A - 1") before generic "Group Stage"
    const m = round.match(/Group\s+([A-Z])\s*[-–]/i);
    if (m) return `Grupo ${m[1].toUpperCase()}`;
    if (round.toLowerCase().includes('group')) return 'Fase de Grupos';
    if (round.includes('Round of 32'))         return 'Dieciseisavos';
    if (round.includes('Round of 16'))         return 'Octavos';
    if (round.includes('Quarter'))             return 'Cuartos';
    if (round.includes('Semi'))                return 'Semifinales';
    if (round.includes('3rd'))                 return '3er Puesto';
    if (round.includes('Final'))               return 'Final';
    return round;
  }

  _logo(team, size = 16) {
    if (team.logo) return html`<img style="width:${size}px;height:${size}px;object-fit:contain" src="${team.logo}" alt="" loading="lazy">`;
    return html`<span style="width:${size}px;height:${size}px;border-radius:50%;background:var(--qgh-fb-bg);display:inline-flex;align-items:center;justify-content:center;font-size:${Math.round(size * .5)}px;font-weight:700;flex-shrink:0">${(team.name || '?').charAt(0)}</span>`;
  }

  render() {
    if (this._loading) return html`
      <div class="widget">
        <div class="team-header">
          <div class="sk sk-circle" style="width:48px;height:48px"></div>
          <div style="flex:1;display:flex;flex-direction:column;gap:8px">
            <div class="sk" style="width:60%;height:16px"></div>
            <div class="sk" style="width:40%;height:10px"></div>
          </div>
        </div>
        ${[...Array(3)].map(() => html`
          <div class="match-row"><div class="sk" style="width:100%;height:10px"></div></div>`)}
        ${this._poweredBy()}
      </div>`;

    if (this._error) return html`
      <div class="widget">
        <div class="state-box">
          <img class="state-logo" src="https://quienganahoy.com/svg/logo_qgh.svg" alt="">
          <span class="state-msg">No se pudo cargar</span>
        </div>
        ${this._poweredBy()}
      </div>`;

    if (!this.team) return html`<div class="widget"><div class="state-box"><span class="state-msg">Falta team ID</span></div>${this._poweredBy()}</div>`;

    const info = this._teamInfo();
    const next = this._nextMatch();
    const id   = Number(this.team);
    const firstRound = this._fixtures[0]?.league?.round || null;
    const grp  = this.group || (firstRound ? this._roundShort(firstRound) : null);

    const groupFixtures = this._fixtures.filter(f =>
      (f.league?.round || '').toLowerCase().includes('group')
    );

    return html`
      <div class="widget">

        <!-- Team header -->
        <div class="team-header">
          ${info?.logo
            ? html`<img class="team-logo-lg" src="${info.logo}" alt="">`
            : html`<div class="team-logo-fb">${(info?.name || '?').charAt(0)}</div>`}
          <div class="team-meta">
            <div class="team-name-lg">${translateCountry(info?.name || '')}</div>
            <div class="team-badges">
              ${grp ? html`<span class="badge">${grp}</span>` : ''}
            </div>
          </div>
        </div>

        <!-- Next match -->
        <div class="section-title">Próximo Partido</div>
        ${next ? (() => {
          const opp    = this._opponent(next);
          const detail = [
            this._roundShort(next.league?.round),
            fmtTime(next.fixture.date),
            next.fixture.venue?.city,
          ].filter(Boolean).join(' — ');
          return html`
            <div class="next-match">
              ${this._logo(opp, 28)}
              <div class="opp-info">
                <div class="opp-name">${translateCountry(opp.name)}</div>
                ${detail ? html`<div class="opp-detail">${detail}</div>` : ''}
              </div>
              <div class="next-when">${this._whenLabel(next)}</div>
            </div>`;
        })() : html`<div class="no-next">Sin próximos partidos</div>`}

        <!-- Group matches -->
        ${groupFixtures.length ? html`
          <div class="section-title">Fase de Grupos</div>
          ${groupFixtures.map(f => {
            const opp   = this._opponent(f);
            const live  = isLive(f.fixture.status.short);
            const score = scoreOrTime(f);
            const ns    = f.fixture.status.short === 'NS' || f.fixture.status.short === 'TBD';
            return html`
              <div class="match-row">
                <span class="match-round">${this._roundShort(f.league?.round)}</span>
                <div class="match-opp">
                  ${opp.logo ? html`<img class="match-opp-logo" src="${opp.logo}" alt="" loading="lazy">` : ''}
                  <span class="match-opp-name">${translateCountry(opp.name)}</span>
                </div>
                ${ns
                  ? html`<span class="match-time">${fmtTime(f.fixture.date)}</span>`
                  : html`<span class="match-score ${live ? 'live' : ''}">${score}</span>`}
              </div>`;
          })}
        ` : ''}

        ${this._poweredBy()}
      </div>`;
  }
}
